import { useSubscription } from "@apollo/client";
import React, { createContext, FC, useState } from "react";
import { NOTIFICATIONS_QUERY } from "../../graphql/query";
import produce from "immer";
import { WritableDraft } from "immer/dist/internal";
import { NotificationType } from "../../graphql/types";
import { NOTIFICATION_SUBSCRIPTION } from "../../graphql/subscription";

type NotificationContextState = {
  loading: boolean;
  count: number;
};

type NotificationNotificationContextState = {
  nextNotificationType?: NotificationType;
  standard: NotificationContextState;
  alert: NotificationContextState;
};

type NotificationSubscriptionContext = {
  notificationContext: NotificationNotificationContextState;
  resetNotificationCount: (notificationType: NotificationType) => void;
};

const INITIAL_STATE: NotificationNotificationContextState = {
  standard: { loading: false, count: 0 },
  alert: { loading: false, count: 0 },
};

export const NotificationSubscriptionContext = createContext<NotificationSubscriptionContext>({
  notificationContext: INITIAL_STATE,
  resetNotificationCount: () => {},
});

export const NotificationSubscriptionProvider: FC = ({ children }) => {
  const [incomingNotification, setIncomingNotification] =
    useState<NotificationNotificationContextState>(INITIAL_STATE);

  const getTarget = (
    draft: WritableDraft<NotificationNotificationContextState>,
    type: NotificationType
  ) => (type === NotificationType.Alert ? draft.alert : draft.standard);

  const handleIncomingNotification = (notificationType: NotificationType) => {
    setIncomingNotification(
      produce((draft) => {
        const target = getTarget(draft, notificationType);
        target.count += 1;
        target.loading = true;
      })
    );
  };

  useSubscription(NOTIFICATION_SUBSCRIPTION, {
    onData: ({ data, client: { cache } }) => {
      const newNotification = data.data?.newNotification;
      if (!newNotification) {
        return;
      }
      handleIncomingNotification(newNotification.type);
      const current = cache.readQuery({
        query: NOTIFICATIONS_QUERY,
        variables: { type: newNotification.type },
      });
      if (!data.data?.newNotification) return;

      const newNotifications = [...(current?.notifications ?? []), data.data.newNotification!].map(
        (x) => ({ ...x, __typename: "Notification" } as const)
      );
      cache.writeQuery({
        query: NOTIFICATIONS_QUERY,
        data: {
          notifications: newNotifications,
        },
        variables: {
          type: newNotification.type,
        },
      });
      setIncomingNotification(
        produce((draft) => {
          getTarget(draft, newNotification.type).loading = false;
          draft.nextNotificationType = newNotification.type;
        })
      );
    },
  });

  return (
    <NotificationSubscriptionContext.Provider
      value={{
        notificationContext: incomingNotification,
        resetNotificationCount: (notificationType: NotificationType) =>
          setIncomingNotification(
            produce((draft) => {
              getTarget(draft, notificationType).count = 0;
            })
          ),
      }}
    >
      {children}
    </NotificationSubscriptionContext.Provider>
  );
};
